<template>
  <nuttrema-layout-form-standard :edit="edit" :saveError="ProgramDetails.saveError">
    <template slot="center">
      <el-form label-width="20em" :model="formData" :rules="rules" :ref="formRef">
        <section>
          <h4 class="section-title">{{ $t('label.basic') }}</h4>
          <div class="section-items">
            <el-form-item :label="$t('label.name')" prop="name">
              <el-col :span="16">
                <el-input v-model="formData.name" :disabled="!edit"></el-input>
              </el-col>
            </el-form-item>

            <template v-if="_get(ProgramDetails, 'data.assigned')">
              <el-form-item
                 prop="start_date"
                :label="$t('label.start_date')">
                <el-date-picker
                   v-model="formData.start_date"
                   type="date"
                   format="dd/MM/yyyy"
                   value-format="yyyy-MM-dd"
                  :clearable="false"
                  :disabled="!edit || !canChangeStartDate"
                  :picker-options="options.start_date">
                </el-date-picker>
              </el-form-item>

              <el-form-item
                 prop="end_date"
                :label="$t('label.end_date')">
                <el-date-picker
                  :value="endDate"
                  disabled
                  type="date"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd">
                </el-date-picker>
              </el-form-item>
            </template>

            <el-form-item
               prop="duration"
               class="el-form-item--remove-line-height"
              :label="$t('label.duration_days')">
              <el-col :span="3">
                <el-input v-model="formData.duration" :disabled="true"></el-input>
              </el-col>
            </el-form-item>

            <el-form-item :label="$t('label.description')" prop="description">
              <el-col :span="16">
                <el-input
                  autosize
                  class="w-100"
                  type="textarea"
                  v-model="formData.description"
                  :autosize="{ minRows: 3, maxRows: 10 }"
                  :disabled="!edit">
                </el-input>
              </el-col>
            </el-form-item>
          </div>
        </section>

        <section>
          <h4 class="section-title">{{ $t('label.categorisation') }}</h4>
          <el-form-item :label="$t('label.general_diet_category')">
            <el-col class="el-col--min-25em" :span="12" >
              <el-cascader
                 v-model="formData.program_type_category_id"
                 class="w-100"
                 popper-class="el-popper--general-category"
                :disabled="!edit"
                :clearable="edit"
                :filterable="true"
                :options="_get(ProgramCategories, 'data', [])"
                :props="{ label: 'name', value: 'id', children: 'SubCategories' }"
                :show-all-levels="false"
                :debounce="500">
              </el-cascader>
            </el-col>
          </el-form-item>
        </section>
      </el-form>
    </template>

    <template slot="footer">
      <div class="footer-row">
        <el-button
           type="primary"
           size="small"
          :loading="isSaving"
          @click="submitForm">
          {{ $t('label.save_changes') }}
        </el-button>
        <el-button type="text" size="small" @click="cancelChanges">{{ $t('label.cancel') }}</el-button>
      </div>
    </template>
  </nuttrema-layout-form-standard>
</template>

<script>
import moment from 'moment';
import { pick, assign, get, last, includes } from 'lodash';

import { STATUS_PROGRAM_CAN_CHANGE_START_DATE } from 'state-machine/program/status-program';

import NuttremaLayoutFormStandard from 'components/ui/layout/nuttrema-layout-form-standard';
import FormStandardBehavior from 'src/components/mixins/form-standard-behavior';

export default {
  name: 'form-diet-basic-information',
  mixins: [
    FormStandardBehavior,
  ],
  props: {
    ProgramDetails: {
      type: Object,
      required: true,
    },
    ProgramCategories: {
      type: Object,
      required: true,
    },
    PersonOccupiedDates: {
      type: Object,
      required: false,
    },
    edit: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  components: {
    NuttremaLayoutFormStandard,
  },
  data() {
    return {
      options: {
        start_date: {
          disabledDate: this.disabledDate,
        },
      },
      formData: {
        name: null,
        description: null,
        start_date: null,
        program_type_category_id: [],
      },
      rules: {
        name: [
          {
            required: true,
            trigger: 'blur',
            message: this.formatValidationMessage('validation.required', 'label.name', true),
          },
        ],
      },
    };
  },
  computed: {
    endDate() {
      const duration = get(this.ProgramDetails, 'data.duration', 0);
      return moment(this.formData.start_date).add(duration - 1, 'days').toDate();
    },
    canChangeStartDate() {
      const status = get(this.ProgramDetails, 'data.status');
      return includes(STATUS_PROGRAM_CAN_CHANGE_START_DATE, status);
    },
  },
  methods: {
    disabledDate(time) {
      const inPast = time.getTime() < moment().subtract(1, 'day').toDate();
      const occupiedDates = get(this.PersonOccupiedDates, 'data', []);
      const wasOccupied = includes(occupiedDates, moment(time).format('YYYY-MM-DD'));
      return inPast || wasOccupied;
    },
    getFormData() {
      const data = {
        name: this.formData.name,
        description: this.formData.description,
        program_type_category_id: last(this.formData.program_type_category_id),
      };

      const assigned = get(this.ProgramDetails, 'data.assigned');
      if (assigned) assign(data, { start_date: this.formData.start_date });

      return data;
    },
    setFormData() {
      const data = get(this.ProgramDetails, 'data', {});
      this.formData = pick(data, [
        'name', 'description', 'duration', 'program_type_category_id', 'start_date',
      ]);
      this.originalData = assign({}, this.formData);
    },
  },
  created() {
    this.stateData = this.ProgramDetails;
    this.formRef = 'form-diet-basic-information';
    this.setFormData();
  },
};
</script>

<style lang="scss">
  .el-popper--general-category {
    .el-cascader-menu {
      min-width: 200px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/professional/programs/diets/details/main-information/form-diet-basic-information.vue